/**
 * Payment Utilities
 * 
 * Helper functions for analyzing cart items and determining payment types
 */

/**
 * Categorize cart items by payment type
 * @param {Array} cartItems - Array of cart items
 * @returns {Object} - Categorized items and totals
 */
export const categorizeCartItems = (cartItems) => {
      const mierosOnlyItems = [];
      const usdOnlyItems = [];
      const mixedItems = [];
      
      let mierosTotal = 0;
      let usdTotal = 0;

      cartItems.forEach(item => {
            // Check multiple possible price locations
            const price = Number(item.price) || Number(item.itemPrice) || Number(item.metadata?.price) || 0;
            
            // For now, we'll treat price === 0 as Mieros-only
            // In the future, items might have both mierosPrice and usdPrice fields
            if (price === 0) {
                  mierosOnlyItems.push(item);
                  // If there's a mierosPrice field, use it; otherwise assume 0
                  const mierosPrice = Number(item.mierosPrice) || 0;
                  mierosTotal += mierosPrice;
            } else {
                  // Check if item also requires Mieros tokens
                  const mierosPrice = Number(item.mierosPrice) || 0;
                  
                  if (mierosPrice > 0) {
                        // Mixed payment: both USD and Mieros
                        mixedItems.push(item);
                        usdTotal += price;
                        mierosTotal += mierosPrice;
                  } else {
                        // USD only
                        usdOnlyItems.push(item);
                        usdTotal += price;
                  }
            }
      });

      return {
            mierosOnlyItems,
            usdOnlyItems,
            mixedItems,
            mierosTotal,
            usdTotal,
            hasMierosOnly: mierosOnlyItems.length > 0,
            hasUsdOnly: usdOnlyItems.length > 0,
            hasMixed: mixedItems.length > 0,
            requiresMieros: mierosOnlyItems.length > 0 || mierosTotal > 0, // If there are Mieros-only items, require Mieros payment
            requiresUsd: usdTotal > 0
      };
};

/**
 * Get payment summary for display
 * @param {Object} categorized - Result from categorizeCartItems
 * @returns {Object} - Summary object with display strings
 */
export const getPaymentSummary = (categorized) => {
      const { mierosTotal, usdTotal, requiresMieros, requiresUsd, hasMierosOnly, hasUsdOnly } = categorized;
      
      // Check for mixed payment first (both Mieros and USD items)
      if (hasMierosOnly && hasUsdOnly) {
            return {
                  type: 'mixed',
                  description: 'Mixed Payment (MiEROS + USD)',
                  mierosAmount: mierosTotal,
                  usdAmount: usdTotal
            };
      }
      
      // Check if there are Mieros-only items (price = 0), even if mierosTotal is 0
      if (hasMierosOnly) {
            return {
                  type: 'mieros',
                  description: mierosTotal > 0 ? 'MiEROS Tokens Only' : 'Free (MiEROS Transfer Only)',
                  mierosAmount: mierosTotal,
                  usdAmount: 0
            };
      }
      
      // USD-only items
      if (hasUsdOnly) {
            return {
                  type: 'usd',
                  description: 'USD Payment Only',
                  mierosAmount: 0,
                  usdAmount: usdTotal
            };
      }
      
      // Fallback (shouldn't happen, but handle gracefully)
      return {
            type: 'usd',
            description: 'USD Payment Only',
            mierosAmount: 0,
            usdAmount: usdTotal
      };
};

/**
 * Calculate total Mieros amount for all items that need token transfer
 * @param {Array} items - Items that require Mieros payment
 * @returns {number} - Total Mieros amount
 */
export const calculateMierosTotal = (items) => {
      return items.reduce((total, item) => {
            const mierosPrice = Number(item.mierosPrice) || (Number(item.price) === 0 ? 0 : 0);
            return total + mierosPrice;
      }, 0);
};

/**
 * Get unique seller IDs from cart items
 * @param {Array} cartItems - Array of cart items
 * @returns {Array} - Array of unique seller IDs
 */
export const getUniqueSellers = (cartItems) => {
      const sellers = new Set();
      cartItems.forEach(item => {
            if (item.sellerID || item.sellerId) {
                  sellers.add(item.sellerID || item.sellerId);
            }
      });
      return Array.from(sellers);
};
